import React, { createContext, useContext, useRef, useCallback, ReactNode } from 'react';
import { Toast } from 'primereact/toast';
import { useTranslation } from 'react-i18next';

interface ToastContextType {
  showSuccess: (detail?: string) => void;
  showError: (detail?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps): React.ReactElement {
  const toastRef = useRef<Toast>(null);
  const { t } = useTranslation();

  const showSuccess = useCallback((detail?: string): void => {
    toastRef.current?.show({
      severity: 'success',
      summary: t('common.success'),
      detail,
      life: 3000
    });
  }, [t]);

  const showError = useCallback((detail?: string): void => {
    toastRef.current?.show({
      severity: 'error',
      summary: t('common.error'),
      detail,
      life: 5000
    });
  }, [t]);

  return (
    <ToastContext.Provider value={{ showSuccess, showError }}>
      <Toast ref={toastRef} position="top-center" />
      {children}
    </ToastContext.Provider>
  );
}

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Extract a human-readable message from an axios error response
export function apiErrorMessage(error: unknown): string | undefined {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    return response?.data?.message;
  }
  return undefined;
}
